import React, { useState, useEffect } from 'react';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../hooks/useToast';
import { supabase } from '../../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { 
  Users, 
  Search, 
  Plus, 
  MoreHorizontal, 
  Edit, 
  Trash, 
  ChevronDown, 
  ChevronUp, 
  ExternalLink, 
  Mail, 
  Phone, 
  BookOpen,
  Save,
  X,
  ArrowUpDown,
  GraduationCap,
  User,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar';
import { 
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';

interface Student {
  id: string;
  full_name: string;
  email: string;
  phone?: string | null;
  student_id?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  postal_code?: string | null;
  preferences?: any | null;
  enrollment_count: number;
  created_at: string;
}

const AdminStudentsPage: React.FC = () => {
  useCheckPermissions(['admin']);
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('full_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(10);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    student_id: '',
    address: '',
    city: '',
    country: '',
    postal_code: ''
  });

  useEffect(() => {
    const { tenantId } = getDomainFilterParams();
    if (tenantId) {
      fetchStudents();
    }
  }, [sortBy, sortOrder]);

  const fetchStudents = async () => {
    const { tenantId } = getDomainFilterParams();
    if (!tenantId) return;
    
    setLoading(true);
    try {
      // Récupérer les étudiants depuis Supabase
      let studentsQuery = supabase
        .from('elearn_customer_profiles')
        .select('*');
      
      studentsQuery = applyDomainFilterToQuery(studentsQuery);
      const { data: studentsData, error: studentsError } = await studentsQuery
        .order(sortBy, { ascending: sortOrder === 'asc' });

      if (studentsError) throw studentsError;

      if (studentsData && studentsData.length > 0) {
        // Pour chaque étudiant, récupérer le nombre d'inscriptions
        const studentsWithCounts = await Promise.all(
          studentsData.map(async (student) => {
            let enrollmentQuery = supabase
              .from('elearn_enrollments')
              .select('id', { count: 'exact', head: true })
              .eq('student_id', student.id);
            
            enrollmentQuery = applyDomainFilterToQuery(enrollmentQuery);
            const { count, error: countError } = await enrollmentQuery;

            return {
              ...student,
              enrollment_count: count || 0
            };
          })
        );

        setStudents(studentsWithCounts);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des étudiants:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les étudiants',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddStudent = () => {
    setEditingStudent(null);
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      student_id: '',
      address: '',
      city: '',
      country: '',
      postal_code: ''
    });
    setIsDialogOpen(true);
  };

  const handleEditStudent = (student: Student) => {
    setEditingStudent(student);
    setFormData({
      full_name: student.full_name,
      email: student.email,
      phone: student.phone || '',
      student_id: student.student_id || '',
      address: student.address || '',
      city: student.city || '',
      country: student.country || '',
      postal_code: student.postal_code || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { tenantId, domain } = getDomainFilterParams();
    if (!tenantId) return;
    
    // Validation simple
    if (!formData.full_name || !formData.email) {
      toast({
        title: 'Erreur de validation',
        description: 'Le nom et l\'email sont requis.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      if (editingStudent) {
        // Mise à jour d'un étudiant existant dans Supabase
        const { error } = await supabase
          .from('elearn_customer_profiles')
          .update({
            full_name: formData.full_name,
            email: formData.email,
            phone: formData.phone || null,
            student_id: formData.student_id || null,
            address: formData.address || null,
            city: formData.city || null,
            country: formData.country || null,
            postal_code: formData.postal_code || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingStudent.id);

        if (error) throw error;
        
        // Mettre à jour l'état local
        const updatedStudents = students.map(student => {
          if (student.id === editingStudent.id) {
            return {
              ...student,
              ...formData,
            };
          }
          return student;
        });
        
        setStudents(updatedStudents);
        toast({
          title: 'Étudiant mis à jour',
          description: `${formData.full_name} a été mis à jour avec succès.`,
        });
      } else {
        // Ajouter un nouvel étudiant dans Supabase
        const newStudentId = uuidv4();
        const { error } = await supabase
          .from('elearn_customer_profiles')
          .insert({
            id: newStudentId,
            tenant_id: tenantId,
            domain: domain,
            full_name: formData.full_name,
            email: formData.email,
            phone: formData.phone || null,
            student_id: formData.student_id || null,
            address: formData.address || null,
            city: formData.city || null,
            country: formData.country || null,
            postal_code: formData.postal_code || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
        
        // Mettre à jour l'état local
        const newStudent: Student = {
          id: newStudentId,
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone || null,
          student_id: formData.student_id || null,
          address: formData.address || null,
          city: formData.city || null,
          country: formData.country || null,
          postal_code: formData.postal_code || null,
          enrollment_count: 0,
          created_at: new Date().toISOString(),
        };
        
        setStudents([...students, newStudent]);
        toast({
          title: 'Étudiant ajouté',
          description: `${formData.full_name} a été ajouté avec succès.`,
        });
      }
      
      // Réinitialiser le formulaire et fermer la boîte de dialogue
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        student_id: '',
        address: '',
        city: '',
        country: '',
        postal_code: ''
      });
      setEditingStudent(null);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'enregistrement.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteStudent = async (studentId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet étudiant ?')) {
      return;
    }
    
    try {
      // Vérifier si l'étudiant a des inscriptions associées
      const { count, error: countError } = await supabase
        .from('elearn_enrollments')
        .select('id', { count: 'exact', head: true })
        .eq('student_id', studentId);

      if (countError) throw countError;

      if (count && count > 0) {
        toast({
          title: 'Suppression impossible',
          description: `Cet étudiant est associé à ${count} inscriptions. Veuillez d'abord supprimer ces inscriptions.`,
          variant: 'destructive',
        });
        return;
      }

      // Supprimer l'étudiant de Supabase
      const { error } = await supabase
        .from('elearn_customer_profiles')
        .delete()
        .eq('id', studentId);

      if (error) throw error;
      
      // Mettre à jour l'état local
      const updatedStudents = students.filter(student => student.id !== studentId);
      setStudents(updatedStudents);
      
      toast({
        title: 'Étudiant supprimé',
        description: 'L\'étudiant a été supprimé avec succès.',
      });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la suppression.',
        variant: 'destructive',
      });
    }
  };

  const filteredStudents = students.filter(student => 
    (student.full_name && student.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (student.email && student.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (student.student_id && student.student_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (student.city && student.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (student.country && student.country.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // Réinitialiser la page courante lorsque les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder]);
  
  // Calculer les indices des étudiants à afficher sur la page courante
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstStudent, indexOfLastStudent);
  
  // Fonction pour changer de page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const getInitials = (name: string) => {
    if (!name || typeof name !== 'string') {
      return 'U'; // User default
    }
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };
  
  // Définition du composant de dialogue pour l'ajout/modification d'étudiants
  const StudentDialog = () => (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent className="bg-gray-900 text-white border-white/10">
        <DialogHeader>
          <DialogTitle>{editingStudent ? 'Modifier l\'étudiant' : 'Nouvel étudiant'}</DialogTitle>
          <DialogDescription>
            {editingStudent 
              ? 'Modifiez les informations de l\'étudiant ci-dessous.' 
              : 'Remplissez les informations pour créer un nouvel étudiant.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Nom complet</Label>
            <Input
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleInputChange}
              placeholder="Nom de l'étudiant"
              className="bg-black/20 border-white/10 text-white"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="email@exemple.com"
              className="bg-black/20 border-white/10 text-white"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="student_id">ID Étudiant</Label>
            <Input
              id="student_id"
              name="student_id"
              value={formData.student_id}
              onChange={handleInputChange}
              placeholder="STU001"
              className="bg-black/20 border-white/10 text-white"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Téléphone</Label>
            <Input
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+33 6 12 34 56 78"
              className="bg-black/20 border-white/10 text-white"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="address">Adresse</Label>
            <Textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="15 rue des Lilas"
              className="bg-black/20 border-white/10 text-white"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">Ville</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="Paris"
                className="bg-black/20 border-white/10 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="postal_code">Code postal</Label>
              <Input
                id="postal_code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleInputChange}
                placeholder="75001"
                className="bg-black/20 border-white/10 text-white"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="country">Pays</Label>
            <Input
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              placeholder="France"
              className="bg-black/20 border-white/10 text-white"
            />
          </div>
          
          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              <X className="mr-2" size={16} />
              Annuler
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              <Save className="mr-2" size={16} />
              {editingStudent ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
  
  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Users className="h-8 w-8 text-[#D4AF37]" />
              Gestion des Étudiants
            </h1>
            <p className="text-gray-400 mt-2">
              {filteredStudents.length} étudiant{filteredStudents.length > 1 ? 's' : ''} trouvé{filteredStudents.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button 
            onClick={handleAddStudent}
            className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90"
          >
            <Plus className="mr-2" size={18} />
            Nouvel étudiant
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{students.length}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Inscriptions</div>
            <div className="text-2xl font-bold text-blue-400">
              {students.reduce((sum, student) => sum + student.enrollment_count, 0)}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Moyenne/étudiant</div>
            <div className="text-2xl font-bold text-[#D4AF37]">
              {students.length > 0 
                ? Math.round(students.reduce((sum, student) => sum + student.enrollment_count, 0) / students.length * 10) / 10
                : 0}
            </div>
          </div>
        </div>

        {/* Recherche */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder="Rechercher par nom, email, ville..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
            />
          </div>
        </div>

        {/* Table des étudiants */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Aucun étudiant trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('full_name')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Étudiant
                        <ArrowUpDown size={14} className={sortBy === 'full_name' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('email')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Contact
                        <ArrowUpDown size={14} className={sortBy === 'email' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Adresse
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('enrollment_count')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Inscriptions
                        <ArrowUpDown size={14} className={sortBy === 'enrollment_count' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('created_at')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Date ajout
                        <ArrowUpDown size={14} className={sortBy === 'created_at' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {currentStudents.map((student, index) => (
                    <tr
                      key={student.id}
                      className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                            <span className="text-[#D4AF37] font-semibold">{getInitials(student.full_name)}</span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {student.full_name}
                            </div>
                            {student.student_id && (
                              <div className="text-sm text-gray-400">ID: {student.student_id}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{student.email}</div>
                        {student.phone && (
                          <div className="text-sm text-gray-400">{student.phone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">
                          {student.city && student.country
                            ? `${student.city}, ${student.country}`
                            : student.city || student.country || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">{student.enrollment_count}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{formatDate(student.created_at)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 text-white">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-gray-900 text-white border-white/10">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem 
                              className="cursor-pointer hover:bg-white/10 text-[#D4AF37]"
                              onClick={() => handleEditStudent(student)}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              <span>Modifier</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="cursor-pointer hover:bg-white/10"
                              onClick={() => window.location.href = `/admin/elearning/enrollments?student=${student.id}`}
                            >
                              <BookOpen className="mr-2 h-4 w-4" />
                              <span>Voir les inscriptions</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem 
                              className="cursor-pointer text-red-500 hover:bg-white/10 hover:text-red-500"
                              onClick={() => handleDeleteStudent(student.id)}
                            >
                              <Trash className="mr-2 h-4 w-4" />
                              <span>Supprimer</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Contrôles de pagination */}
          {filteredStudents.length > 0 && (
              <div className="flex justify-between items-center p-4 border-t border-white/10">
                <div className="text-sm text-white/70">
                  Étudiants ({filteredStudents.length}) - Page {currentPage}/{totalPages}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Afficher les pages autour de la page courante
                    let pageToShow;
                    if (totalPages <= 5) {
                      // Si moins de 5 pages, afficher toutes les pages
                      pageToShow = i + 1;
                    } else if (currentPage <= 3) {
                      // Si on est au début, afficher les 5 premières pages
                      pageToShow = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // Si on est à la fin, afficher les 5 dernières pages
                      pageToShow = totalPages - 4 + i;
                    } else {
                      // Sinon, afficher 2 pages avant et 2 pages après la page courante
                      pageToShow = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageToShow}
                        variant={currentPage === pageToShow ? "default" : "outline"}
                        size="sm"
                        onClick={() => paginate(pageToShow)}
                        className={currentPage === pageToShow 
                          ? "bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white" 
                          : "border-white/10 text-white hover:bg-white/5"}
                      >
                        {pageToShow}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
          )}
        </div>
        
        {/* Render StudentDialog component */}
        <StudentDialog />
      </div>
    </AdminLayout>
  );
};

export default AdminStudentsPage;
